import { useNavigate } from 'react-router-dom'
import { CircleUser, LifeBuoy, LogOut, Settings, Sun } from 'lucide-react'
import { Avatar, Dropdown } from '@/components/ui'
import { applyTheme, useSessionStore, useUiStore } from '@/stores'
import { SUPPORT_EMAIL } from '@/internationalization/constants'
import { useT } from '@/internationalization/i18n'
import { api } from '@/internationalization/api-client'
import { queryClient } from '@/app/query-client'

export function UserMenu() {
  const t = useT()
  const navigate = useNavigate()
  const user = useSessionStore((s) => s.user)
  const signOut = useSessionStore((s) => s.signOut)
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)

  if (!user) return null

  const cycleTheme = () => {
    const next = theme === 'light' ? 'dark' : theme === 'dark' ? 'system' : 'light'
    setTheme(next)
    applyTheme(next)
  }

  return (
    <Dropdown
      heading={user.email}
      trigger={({ toggle }) => (
        <button
          type="button"
          onClick={toggle}
          aria-label={t('userMenu.accountMenu')}
          className="rounded-full transition-opacity hover:opacity-85"
        >
          <Avatar name={user.name} src={user.avatarUrl} size="sm" useSpeakerColor />
        </button>
      )}
      items={[
        { id: 'profile', label: t('userMenu.yourProfile'), icon: <CircleUser />, onSelect: () => navigate('/settings/profile') },
        { id: 'settings', label: t('userMenu.workspaceSettings'), icon: <Settings />, onSelect: () => navigate('/settings') },
        // Duplicated from ThemeToggle on purpose: the toggle is hidden below sm,
        // and the account menu is the only control that survives there.
        { id: 'theme', label: t('userMenu.appearance', { theme: t(`theme.value.${theme}`) }), icon: <Sun />, onSelect: cycleTheme },
        {
          id: 'support',
          label: t('userMenu.contactSupport'),
          icon: <LifeBuoy />,
          separatorBefore: true,
          onSelect: () => window.open(`mailto:${SUPPORT_EMAIL}`, '_blank'),
        },
        {
          id: 'signout',
          label: t('userMenu.signOut'),
          icon: <LogOut />,
          destructive: true,
          separatorBefore: true,
          onSelect: () => {
            void api.post('/auth/logout').finally(() => {
              signOut()
              // EMPTIED, NOT LEFT TO AGE OUT. `signOut` clears the token and
              // the store and never touched the cache, so every meeting,
              // transcript and action item this person read stayed in memory
              // for its `gcTime` -- and the audio query holds a PRESIGNED URL,
              // a bearer credential for a recording that keeps working until
              // it expires. On a shared machine the next person to sign in
              // without reloading would be served it from cache.
              queryClient.clear()
              navigate('/login')
            })
          },
        },
      ]}
    />
  )
}
